import { useMemo, useState } from "react";
import { styles } from "../../../utils/styles/products-maintenance-styles";
import { useI18n } from "@/i18n/I18nProvider";
import { useFormik } from "formik";
import Box from "@mui/material/Box";
import dynamic from "next/dynamic";
import CalcPopup from "@/components/products-revamp/CalcPopup";
import ProductMain from "@/components/products-revamp/product-main";
import { colors } from "@/utils/const";
import DynamicFAQSection from "@/components/DynamicFAQSection";
import StepsRow from "@/components/steps-row";
import SectionContentSide from "@/components/products-revamp/section-content-side";
import Grid from "@mui/material/Grid";
import OutImgSection from "@/components/out-img-section";
import WhyContact from "@/components/products-revamp/why-contact";
import ApplyProduct from "@/components/products-revamp/product-apply";
import Features from "@/components/products-revamp/features-section";
import { emptyProps, getProductPageData } from "@/services/get-product-data";
import {
  getLocalizedValue,
  getStaticTextByKey,
} from "@/utils/general-functions";
import { useSplitContentSections } from "@/hooks/product";
import { useProductLoanPackages } from "@/hooks/useProductLoanPackages";
import { getProductsText } from "../../../../locales/products";
const PageHead = dynamic(() => import("@/components/PageHead"));

export default function Index({
  heroData,
  contentWithImageData,
  faqData,
  stepsData,
  whycontactData,
  staticTextData,
  featuresData,
  mainData,
}) {
  const { t, locale } = useI18n();
  const products = getProductsText(locale);
  const isArabic = locale === "ar";
  const [openCalc, setOpenCalc] = useState(false);

  const onSubmit = async (values, actions) => {
    await new Promise((resolve) => setTimeout(resolve, 5000));
    actions.resetForm();
  };
  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleBlur,
    handleChange,
    handleSubmit,
  } = useFormik({
    initialValues: {
      email: "",
      phoneNumber: "",
      governorate: "",
      area: "",
      username: "",
      lastname: "",
      car_type: "",
      car_price: "",
    },
    onSubmit,
  });
  const { targetProduct, data } = useProductLoanPackages(
    products.motorcycle.id,
  );
  const staticTexts = getStaticTextByKey(staticTextData?.data);
  const { fullNavySections, splitSections } =
    useSplitContentSections(contentWithImageData);
  const thirdSection = useMemo(() => {
    return {
      title: fullNavySections[0]?.title?.text?.[locale],
      titleType: fullNavySections[0]?.title?.text?.type,
      description: fullNavySections[0]?.description?.text?.[locale],
      pgType: fullNavySections[0]?.description?.type,
      // subtitle:
      //   staticTexts.motorcycles_content_img_section_subTitle.value.text[locale],
      subtitle:
        staticTexts?.motorcycles_content_img_section_subTitle.value.text[
          locale
        ] || "",

      footer:
        staticTexts?.motorcycles_content_img_section_subDesc.value.text[
          locale
        ] || "",
    };
  }, [locale]);
  return (
    <>
      <PageHead
        title={getLocalizedValue(mainData[0], "meta_title", locale)}
        description={getLocalizedValue(mainData[0], "meta_description", locale)}
        scripts={getLocalizedValue(mainData[0], "schema", locale)}
      />

      <Box sx={{ ...styles.container, overflow: "hidden" }}>
        {heroData?.length > 0 && (
          <ProductMain
            heroData={heroData}
            staticTexts={staticTexts}
            onApplyClick={() => {
              const formSection = document.getElementById("apply-form");
              if (formSection) {
                formSection.scrollIntoView({ behavior: "smooth" });
              }
            }}
            onCalcClick={() => setOpenCalc((prev) => !prev)}
            onExploreMoreClick={() => {
              const formSection = document.getElementById(
                "motorcycles-features",
              );
              if (formSection) {
                formSection.scrollIntoView({ behavior: "smooth" });
              }
            }}
            imgStyle={{ mt: locale === "ar" ? 7 : { xs: 5, xl: 6 } }}
            headerH1={true}
            shadow={true}
          />
        )}
        <Box sx={{ position: "relative", bgcolor: colors.white }}>
          {/* first section */}
          {/**Features section */}
          {featuresData?.length > 0 &&
            featuresData.map((section, index) => (
              <Features
                key={section?.id || index}
                section={section}
                id="motorcycles-features"
                bgHeight={{
                  xs: "68%",
                  md: "40%",
                  lg: "38%",
                }}
              />
            ))}
          {/* second section */}
          <Box
            component="section"
            className="horizontal-safe-padding vertical-safe-padding"
            sx={{ overflow: "hidden" }}
          >
            <Grid
              container
              spacing={{ xs: 4, md: 2 }}
              alignItems="center"
              sx={{
                flexDirection: {
                  xs: "column-reverse",
                  md: "row",
                },
              }}
            >
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  position: "relative",
                  display: "flex",
                  justifyContent: {
                    xs: "center",
                    md: !isArabic ? "flex-start" : "flex-end",
                  },
                }}
              >
                <OutImgSection
                  img={fullNavySections[0].img}
                  altImg={fullNavySections[0].title}
                  side={isArabic ? "right" : "left"}
                  customStyle={{
                    transform: {
                      lg: isArabic ? "translateX(18%)" : "translateX(-18%) ",
                      xl: isArabic ? "translateX(0%)" : "translateX(0%)",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={8}>
                <SectionContentSide content={thirdSection} />
              </Grid>
            </Grid>
          </Box>

          {/**third section */}
          <WhyContact whycontactData={whycontactData} />
          {/**faq section */}
          {faqData?.length > 0 &&
            faqData.map((section, index) => (
              <DynamicFAQSection
                key={section?.id || index}
                title={
                  staticTexts?.faqs_section_title?.value?.text[locale] || ""
                }
                items={section?.faqs}
                renderHTML={true}
                variantTitle="h2"
                variantQ="h3"
              />
            ))}

          {/**Steps section */}
          {stepsData?.length > 0 &&
            stepsData.map((section, index) => (
              <StepsRow
                key={section.id || index}
                title={section?.title?.text?.[locale]}
                titleType={section?.title?.type}
                subtitle={section?.description?.text?.[locale]}
                subtitleType={section?.description?.type}
                steps={section?.items}
              />
            ))}
        </Box>

        <ApplyProduct
          img={heroData[0]?.img}
          title={mainData[0]?.form_title.text[locale]}
          titleType={mainData[0]?.form_title?.type}
          description={mainData[0]?.form_desc.text[locale]}
          descType={mainData[0]?.form_desc?.type}
          formType="b"
          product="21"
          source={30}
          shadow={true}
        />
        {targetProduct?.hasPlan && (
          <CalcPopup
            open={openCalc}
            onClose={() => setOpenCalc(false)}
            data={data}
            max_amount={targetProduct?.maxLimit}
            price_label={products.motorcycle.price}
            price_desc={products.motorcycle.price_desc}
          />
        )}
      </Box>
    </>
  );
}

export async function getServerSideProps() {
  try {
    const data = await getProductPageData("motorcycles");
    return { props: data };
  } catch {
    return { props: emptyProps };
  }
}
